import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { toast } from "react-hot-toast";
import CompanyLayout from "../../components/company/CompanyLayout";

/* ────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────── */

function formatCurrency(v) {
  const n = Number(v) || 0;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function formatDateWeek(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateDay(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Today as YYYY-MM-DD */
function todayStr() {
  return new Date().toISOString().split("T")[0];
}

/** 90 days ago as YYYY-MM-DD */
function ninetyDaysAgoStr() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().split("T")[0];
}

/** Start of current year */
function yearStartStr() {
  return `${new Date().getFullYear()}-01-01`;
}


/* ────────────────────────────────────────────────────────────
   KPI Card
   ──────────────────────────────────────────────────────────── */

function KpiCard({ label, value, color = "text-ink", sub }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-5">
      <p className="text-xs text-tertiary mb-1">{label}</p>
      <p className={`text-2xl font-semibold tabular-nums ${color}`}>{value}</p>
      {sub && <p className="text-xs text-faint mt-1">{sub}</p>}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
   Simple Bar Chart (pure CSS)
   ──────────────────────────────────────────────────────────── */

function BarChart({ buckets, period }) {
  if (!buckets || buckets.length === 0) return null;

  const maxVal = Math.max(...buckets.map((b) => Math.max(Math.abs(b.expense), Math.abs(b.revenue))), 1);

  const labelFn = period === "day" ? formatDateDay : period === "week" ? formatDateWeek : formatDate;

  return (
    <div className="bg-surface border border-line rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-secondary">Revenue vs Expenses</h3>
        <div className="flex items-center gap-4 text-xs text-faint">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#1B8A5A] inline-block" />
            Revenue
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-red-400 inline-block" />
            Expenses
          </span>
        </div>
      </div>

      <div className="flex items-end gap-1 sm:gap-2 h-44 overflow-x-auto pb-1">
        {buckets.map((b, i) => {
          const revH = maxVal > 0 ? (b.revenue / maxVal) * 100 : 0;
          const expH = maxVal > 0 ? (b.expense / maxVal) * 100 : 0;

          return (
            <div
              key={b.key}
              className="flex flex-col items-center flex-1 min-w-[40px] max-w-[80px] group"
            >
              {/* Bars */}
              <div className="flex items-end gap-0.5 w-full h-32">
                <div
                  className="flex-1 bg-[#1B8A5A]/80 rounded-t transition-all duration-300 group-hover:bg-[#1B8A5A]"
                  style={{ height: `${Math.max(revH, 1)}%` }}
                  title={`Revenue: ${formatCurrency(b.revenue)}`}
                />
                <div
                  className="flex-1 bg-red-400/80 rounded-t transition-all duration-300 group-hover:bg-red-300"
                  style={{ height: `${Math.max(expH, 1)}%` }}
                  title={`Expenses: ${formatCurrency(b.expense)}`}
                />
              </div>
              {/* Label */}
              <span className="text-[0.625rem] text-faint mt-1.5 leading-tight [overflow-wrap:anywhere] w-full text-center">
                {labelFn(b.key)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
   Main page
   ──────────────────────────────────────────────────────────── */

export default function CompanyFinanceIncomePage() {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buckets, setBuckets] = useState([]);
  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState(yearStartStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [exporting, setExporting] = useState(false);

  /* ── Load org ── */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth("/api/accounts/me/");
        const data = res?.json ? await res.json() : res;
        setOrg(data?.organization || null);
      } catch (err) {
        console.error("Failed to load org", err);
      }
    })();
  }, []);

  /* ── Load income data when deps change ── */
  useEffect(() => {
    if (org) loadIncome();
  }, [org, period, startDate, endDate]);

  const loadIncome = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("period", period);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);

      const url = `/api/contracts/finance/income-statement/?${params}`;
      const res = await fetchWithAuth(url);
      const data = res?.json ? await res.json() : res;

      setBuckets(data.buckets || []);
    } catch (err) {
      console.error("Failed to load income statement", err);
      toast.error("Failed to load income statement");
    } finally {
      setLoading(false);
    }
  };

  /* ── KPI totals ──
     Backend ships each bucket with revenue / expense / fee / net where
     net = revenue − expense − fee. We previously ignored `fee` here,
     which made the Total Expenses card under-report (no platform fee
     folded in) and the Net Income card over-report (didn't subtract
     the fee), even while each per-bucket row showed the correct net
     because it just reads `b.net`. Fold fee in so the headline numbers
     match the row math. */
  const totals = useMemo(() => {
    let revenue = 0;
    let labor = 0;
    let fee = 0;
    for (const b of buckets) {
      revenue += b.revenue || 0;
      labor += b.expense || 0;
      fee += b.fee || 0;
    }
    return {
      revenue,
      labor,
      fee,
      expense: labor + fee,          // total outflows — what the card displays
      net: revenue - labor - fee,    // matches per-bucket net from the backend
    };
  }, [buckets]);

  /* ── CSV export ── */
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append("period", period);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);

      const url = `/api/contracts/finance/income-statement/export.csv?${params}`;
      const res = await fetchWithAuth(url);

      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `income_statement_${startDate}_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success("Exported successfully");
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  /* ── Quick date presets ── */
  const setPreset = (preset) => {
    const today = new Date();
    let s, e;
    switch (preset) {
      case "ytd":
        s = `${today.getFullYear()}-01-01`;
        e = todayStr();
        break;
      case "last90":
        s = ninetyDaysAgoStr();
        e = todayStr();
        break;
      case "lastYear": {
        const y = today.getFullYear() - 1;
        s = `${y}-01-01`;
        e = `${y}-12-31`;
        break;
      }
      case "all":
        s = "2020-01-01";
        e = todayStr();
        break;
      default:
        return;
    }
    setStartDate(s);
    setEndDate(e);
  };

  /* ── Format label per period ── */
  const labelFn = period === "day" ? formatDateDay : period === "week" ? formatDateWeek : formatDate;

  /* ── Header ── */
  const header = (
    <div>
      <h1 className="text-2xl sm:text-3xl font-display italic text-ink">Income statement</h1>
      <p className="text-sm text-tertiary mt-1">
        Revenue, expenses, and net income by period
      </p>
    </div>
  );

  return (
    <CompanyLayout org={org} header={header}>
      <div className="space-y-6 p-4 sm:p-6">

        {/* ─── Controls ─── */}
        <div className="flex flex-col gap-4">
          {/* Top row: filters + export */}
          <div className="flex flex-col lg:flex-row gap-3 lg:items-end justify-between">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Period */}
              <div>
                <label htmlFor="company-finance-income-page-group-by" className="text-xs text-tertiary mb-1 block">Group by</label>
                <select id="company-finance-income-page-group-by"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="px-3 py-2 bg-surface border border-line-control rounded text-ink text-sm focus:outline-none focus:border-brand [color-scheme:light]"
                >
                  <option value="month">Month</option>
                  <option value="week">Week</option>
                  <option value="day">Day</option>
                </select>
              </div>

              {/* Start date */}
              <div>
                <label htmlFor="company-finance-income-page-from" className="text-xs text-tertiary mb-1 block">From</label>
                <input id="company-finance-income-page-from"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 bg-surface border border-line-control rounded text-ink placeholder-faint text-sm focus:outline-none focus:border-brand [color-scheme:light]"
                />
              </div>

              {/* End date */}
              <div>
                <label htmlFor="company-finance-income-page-to" className="text-xs text-tertiary mb-1 block">To</label>
                <input id="company-finance-income-page-to"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 bg-surface border border-line-control rounded text-ink placeholder-faint text-sm focus:outline-none focus:border-brand [color-scheme:light]"
                />
              </div>
            </div>

            {/* Export */}
            <button
              onClick={handleExportCSV}
              disabled={buckets.length === 0 || exporting}
              className="px-4 py-2 bg-brand text-white rounded-pill hover:bg-brand-hover disabled:bg-sunken disabled:text-faint disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 w-fit"
            >
              {exporting ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              Export CSV
            </button>
          </div>

          {/* Date presets */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "ytd", label: "Year to Date" },
              { key: "last90", label: "Last 90 Days" },
              { key: "lastYear", label: "Last Year" },
              { key: "all", label: "All Time" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setPreset(key)}
                className="text-xs px-3 py-1.5 rounded-pill border border-line-strong bg-surface text-secondary hover:text-brand hover:border-brand transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ─── Loading ─── */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-line-strong border-t-brand" />
          </div>
        ) : buckets.length === 0 ? (
          /* ─── Empty state ─── */
          <div className="bg-surface border border-line rounded-2xl p-12 text-center">
            <svg className="w-12 h-12 mx-auto mb-4 text-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-ink mb-2">No financial data for this period</h3>
            <p className="text-sm text-tertiary max-w-md mx-auto">
              Income statement entries appear once payments have been processed. Try expanding
              the date range or check that timecards and milestones have been approved.
            </p>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => setPreset("all")}
                className="text-sm px-4 py-2 rounded-pill border border-line-strong bg-surface text-ink hover:text-brand hover:border-brand transition-colors"
              >
                Show all time
              </button>
              <Link
                to="/company/accounting"
                className="text-sm px-4 py-2 rounded-pill border border-line-strong bg-surface text-ink hover:text-brand hover:border-brand transition-colors no-underline"
              >
                Back to accounting
              </Link>
            </div>
          </div>
        ) : (
          /* ─── Data display ─── */
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <KpiCard
                label="Total revenue"
                value={formatCurrency(totals.revenue)}
                color={totals.revenue > 0 ? "text-success-text" : "text-secondary"}
              />
              <KpiCard
                label="Total expenses"
                value={formatCurrency(totals.expense)}
                color="text-red-500"
                sub={
                  totals.fee > 0
                    ? `${formatCurrency(totals.labor)} labor + ${formatCurrency(totals.fee)} platform fee`
                    : undefined
                }
              />
              <KpiCard
                label="Net income"
                value={formatCurrency(totals.net)}
                color={totals.net >= 0 ? "text-success-text" : "text-red-500"}
                sub={
                  totals.revenue > 0
                    ? `${((totals.net / totals.revenue) * 100).toFixed(1)}% margin`
                    : undefined
                }
              />
            </div>

            {/* Chart */}
            <BarChart buckets={buckets} period={period} />

            {/* Table */}
            <div className="bg-surface border border-line rounded-2xl overflow-hidden">
              {/* Mobile cards */}
              <div className="block lg:hidden divide-y divide-line">
                {buckets.map((b) => (
                  <div key={b.key} className="p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-ink">{labelFn(b.key)}</span>
                      <span
                        className={`text-lg font-semibold tabular-nums ${
                          b.net >= 0 ? "text-success-text" : "text-red-500"
                        }`}
                      >
                        {formatCurrency(b.net)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-tertiary">
                      <span>Rev: {formatCurrency(b.revenue)}</span>
                      <span>Exp: {formatCurrency(b.expense)}</span>
                      {(b.fee || 0) > 0 && (
                        <span>Fee: {formatCurrency(b.fee)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <table className="hidden lg:table w-full">
                <thead>
                  <tr className="bg-sunken border-b border-line">
                    <th className="px-6 py-3 text-left text-xs text-tertiary">
                      Period
                    </th>
                    <th className="px-6 py-3 text-right text-xs text-tertiary">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-right text-xs text-tertiary">
                      Expenses
                    </th>
                    <th className="px-6 py-3 text-right text-xs text-tertiary">
                      Platform fee
                    </th>
                    <th className="px-6 py-3 text-right text-xs text-tertiary">
                      Net
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {buckets.map((b) => (
                    <tr key={b.key} className="hover:bg-sunken transition-colors">
                      <td className="px-6 py-3.5 text-sm text-ink">{labelFn(b.key)}</td>
                      <td className="px-6 py-3.5 text-sm text-right tabular-nums text-success-text">
                        {formatCurrency(b.revenue)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right tabular-nums text-red-500">
                        {formatCurrency(b.expense)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right tabular-nums text-red-500/80">
                        {formatCurrency(b.fee || 0)}
                      </td>
                      <td
                        className={`px-6 py-3.5 text-sm text-right tabular-nums font-medium ${
                          b.net >= 0 ? "text-success-text" : "text-red-500"
                        }`}
                      >
                        {formatCurrency(b.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Totals row */}
                <tfoot>
                  <tr className="border-t-2 border-line-strong bg-sunken">
                    <td className="px-6 py-3.5 text-sm font-semibold text-ink">Total</td>
                    <td className="px-6 py-3.5 text-sm text-right tabular-nums font-semibold text-success-text">
                      {formatCurrency(totals.revenue)}
                    </td>
                    <td className="px-6 py-3.5 text-sm text-right tabular-nums font-semibold text-red-500">
                      {formatCurrency(totals.labor)}
                    </td>
                    <td className="px-6 py-3.5 text-sm text-right tabular-nums font-semibold text-red-500/80">
                      {formatCurrency(totals.fee)}
                    </td>
                    <td
                      className={`px-6 py-3.5 text-sm text-right tabular-nums font-semibold ${
                        totals.net >= 0 ? "text-success-text" : "text-red-500"
                      }`}
                    >
                      {formatCurrency(totals.net)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile total */}
            <div className="block lg:hidden bg-surface border border-line rounded-2xl p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-ink">Total net</span>
                <span
                  className={`text-xl font-bold tabular-nums ${
                    totals.net >= 0 ? "text-success-text" : "text-red-500"
                  }`}
                >
                  {formatCurrency(totals.net)}
                </span>
              </div>
            </div>
          </>
        )}

        {/* ─── Navigation ─── */}
        {!loading && (
          <div className="flex items-center gap-3 text-sm pt-2">
            <Link
              to="/company/accounting"
              className="text-brand hover:text-brand-hover transition-colors no-underline"
            >
              ← Back to Accounting
            </Link>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
}